import React, { useContext, useEffect, useState } from 'react';
import uuid from 'uuid';
import _ from 'lodash';
import ShortUniqueId from 'short-unique-id';

import { ProjectSettings } from 'components/organisms/CourseDashboard';
import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useUser } from 'contexts/user';
import { useViewport } from 'contexts/viewport';
import withResponsiveLayout from 'hoc/withResponsive';
import { PageContext } from 'pages/CourseDashboard/PageProvider';
import Mobile from 'pages/CourseDashboard/Password/mobile';
import Tablet from 'pages/CourseDashboard/Password/tablet';
import Desktop from 'pages/CourseDashboard/Password/desktop';
import accountRequests from 'services/account';
import batchRequests from 'services/batch';
import courseRequests from 'services/course';
import { AlertDialog, ResponsiveDialog } from 'shell/components';
import CloningContents from 'components/organisms/CourseDashboard/CloningContents';
import { COURSE_ID_DICTIONARY } from 'utils/helper';

const getActiveCourse = (course) => {
    return course
        ? {
              ...course,
              field_of_study: course?.field_of_study || '',
          }
        : null;
};

const getProject = (course) => {
    return {
        title: course?.project_title || '',
        start_on: course?.start_on || 0,
        end_on: course?.end_on || 0,
    };
};

const PageLayout = () => {
    const { device } = useViewport();
    const { selectAccount, startCourseSetup } = useAuth();
    const { message } = useSnackbar();
    const { findAllCourses } = useUser();
    const { width } = useViewport();

    const { cloningContentsDialog, setCloningContentsDialog, dialog, setDialog, generatedIdList, setGeneratedIdList } =
        useContext(PageContext);

    const [isLoading, setIsLoading] = useState(true);
    const [courses, setCourses] = useState(null);
    const [activeCourse, setActiveCourse] = useState(null);
    const [courseGroups, setCourseGroups] = useState(null);
    const [courseAccounts, setCourseAccounts] = useState(null);
    const [courseUsers, setCourseUsers] = useState(null);
    const [projectSettings, setProjectSettings] = useState({ isOpen: false });

    const updateProjectSettings = async (form) => {
        const updatedCourse = { project_title: form.title, start_on: form.start_on, end_on: form.end_on };
        setCourses((current) => {
            return current.map((course) => {
                return course.course_id === activeCourse.course_id ? { ...course, ...updatedCourse } : course;
            });
        });
        setActiveCourse((current) => ({
            ...current,
            project_title: form.title,
            start_on: form.start_on,
            end_on: form.end_on,
        }));
        await courseRequests.updateCourse(activeCourse.course_id, updatedCourse);
        message.success('Project settings updated successfully.');
    };

    const handleGenerateId = async () => {
        const generatedId = new ShortUniqueId({
            length: 10,
            dictionary: COURSE_ID_DICTIONARY,
        })();
        await courseRequests.updateCourse(activeCourse.course_id, { generated_id: generatedId });
        const updatedCourse = { ...activeCourse, generated_id: generatedId };
        setCourses((current) => {
            return current.map((course) => {
                return course.course_id === activeCourse.course_id ? { ...course, ...updatedCourse } : course;
            });
        });
        setActiveCourse((current) => ({
            ...current,
            generated_id: generatedId,
        }));
    };

    const handleProjectCardDropdown = ({ key }) => {
        switch (key) {
            case 'clone-contents': {
                return setCloningContentsDialog((current) => ({
                    ...current,
                    open: true,
                    message: `Delete 'this course'?`,
                    description:
                        'This course will be deleted permanently from our system, and users will lose access immediately. Please note that this action cannot be undone.',
                    variant: 'destructive',
                    actionButtonText: 'Delete',
                    onAction: () => {
                        setCloningContentsDialog((current) => ({ ...current, open: false, onAction: () => {} }));
                    },
                }));
            }
            case 'edit-project-details': {
                return setProjectSettings({ isOpen: true });
            }
            default:
                return;
        }
    };

    const deleteCourse = async (courseId) => {
        setCourses((prev) => prev.filter((course) => course.course_id !== courseId));
        if (!courses.length) {
            setIsLoading(false);
        } else {
            setActiveCourse(courses[0]);
        }
        await courseRequests.deleteCourse(courseId, { course_id: courseId });
        return message.success(`${_.capitalize(activeCourse.name || 'The new course')} was deleted successfully.`);
    };

    const openDeleteCourseDialog = (course) => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: `Delete ${course.name || 'this course'}?`,
            description:
                'This course will be deleted permanently from our system, and users will lose access immediately. Please note that this action cannot be undone.',
            variant: 'destructive',
            actionButtonText: 'Delete',
            onAction: () => {
                setDialog((current) => ({ ...current, open: false, onAction: () => {} }));
                return deleteCourse(course.course_id);
            },
        }));
    };

    const updateCourseSettings = async (form) => {
        const updatedCourse = {
            school: form.school,
            faculty: form.faculty,
            name: form.name,
            semester: form.semester,
            section: form.section,
            field_of_study: form.field_of_study,
        };
        setCourses((current) => {
            return current.map((course) => {
                return course.course_id === activeCourse.course_id ? { ...course, ...updatedCourse } : course;
            });
        });
        setActiveCourse((current) => ({ ...current, ...updatedCourse }));
        await courseRequests.updateCourse(activeCourse.course_id, updatedCourse);
        message.success('Course details updated successfully.');
    };

    const sendInvitations = async (type, usersToInvite) => {
        await batchRequests.batchSendInvitations({
            data: {
                is_lti: false,
                root_url: window.location.origin,
                course_id: activeCourse.course_id,
                type,
                users: usersToInvite.map((userToInvite) => {
                    const { email, first_name, last_name } = userToInvite;
                    const user = courseUsers.find((user) => user.email === email);
                    const account = courseAccounts.find((account) => account.user_id === user?.id);
                    return {
                        id: account?.id || uuid.v4(),
                        user_id: user?.id || uuid.v4(),
                        email: email.toLowerCase(),
                        first_name,
                        last_name,
                    };
                }),
            },
        });
        const newCourseAccounts = await courseRequests.findCourseAccounts(activeCourse.course_id);
        const newCourseUsers = await courseRequests.findCourseUsers(activeCourse.course_id);
        setCourseAccounts(newCourseAccounts);
        setCourseUsers(newCourseUsers);
    };

    const removeUser = async (accountUser) => {
        setCourseAccounts((current) => current.filter(({ id }) => id !== accountUser.owner_account_id));
        setCourseUsers((current) => current.filter(({ id }) => id !== accountUser.user_id));
        await accountRequests.deleteAccount(accountUser.id || accountUser.owner_account_id);
    };

    const accountUsers =
        courseAccounts && courseUsers
            ? courseAccounts
                  .filter((account) => account.active)
                  .map((account) => {
                      const user = courseUsers.find((user) => user.id === account.user_id) || {};
                      const userAccounts = courseAccounts.filter(({ user_id }) => user_id === user.id);
                      const isPending = userAccounts.some(({ login_activity }) => Boolean(login_activity));
                      return { ...user, ...account, owner_account_id: account.id, is_pending: isPending };
                  })
            : [];

    useEffect(() => {
        (async () => {
            const data = await findAllCourses();
            const accountCourses = data.accounts
                .filter((account) => account.active)
                .map((account) => {
                    const course = data.courses
                        .filter((course) => course.active !== false) // TODO some old course documents created from self serve admin are missing `active`
                        .find((course) => course.id === account.course_id);
                    return {
                        user_id: account.user_id,
                        owner_account_id: account.id,
                        role: account.role,
                        course_id: account.course_id,
                        generated_id: course.generated_id || null,
                        group_id: account.group_id,
                        school: course.school,
                        faculty: course.faculty,
                        name: course.name,
                        semester: course.semester,
                        field_of_study: course.field_of_study,
                        section: course.section,
                        project_title: course.project_title,
                        start_on: course.start_on,
                        end_on: course.end_on,
                        finished_course_set_up: course.finished_course_set_up !== false,
                        group_formation: course.group_formation || null,
                        survey: course.survey || null,
                        finished_group_generation: course.finished_group_generation || false,
                    };
                });

            const courseDrafts = await Promise.all(
                accountCourses.map(async ({ course_id }) => {
                    return await courseRequests.findCourseDraft(course_id);
                })
            );
            const courses = accountCourses.map((accountCourse) => {
                const courseDraft = courseDrafts.find(
                    (courseDraft) => courseDraft?.course_id === accountCourse.course_id
                );
                if (!courseDraft) return accountCourse;
                return {
                    ...accountCourse,
                    school: accountCourse.school || courseDraft.school,
                    faculty: accountCourse.faculty || courseDraft.faculty,
                    name: accountCourse.name || courseDraft.course_name,
                    semester: accountCourse.semester || courseDraft.semester,
                    section: accountCourse.section || courseDraft.section,
                    field_of_study: accountCourse.field_of_study || courseDraft.field_of_study,
                    project_title: accountCourse.project_title || courseDraft.project_title,
                    start_on: accountCourse.start_on || courseDraft.start_on,
                    end_on: accountCourse.end_on || courseDraft.end_on,
                };
            });
            setCourses(courses);
            if (!courses.length) {
                setIsLoading(false);
            } else {
                setActiveCourse(courses[0]);
            }
        })();
    }, []);

    useEffect(() => {
        if (courses && device !== 'mobile' && !activeCourse) {
            setActiveCourse(courses[0]);
        }
    }, [device]);

    useEffect(() => {
        if (!activeCourse?.course_id) return;
        (async () => {
            setIsLoading(true);
            const courseGroups = await courseRequests.findCourseGroups(activeCourse.course_id);
            const courseAccounts = await courseRequests.findCourseAccounts(activeCourse.course_id);
            const courseUsers = await courseRequests.findCourseUsers(activeCourse.course_id);
            setCourseGroups(courseGroups);
            setCourseAccounts(courseAccounts);
            setCourseUsers(courseUsers);
            setIsLoading(false);
        })();
    }, [activeCourse?.course_id]);

    const props = {
        isLoading,
        courses,
        activeCourse: getActiveCourse(activeCourse),
        setActiveCourse,
        startCourseSetup: () => {
            if (courses.length >= 10) return message.error('You have reached the maximum limit of number of courses.');
            startCourseSetup();
        },
        openDeleteCourseDialog,

        role: activeCourse?.role,

        project: getProject(activeCourse),
        numGroups: courseGroups?.length,
        selectProject: () => {
            setIsLoading(true);
            selectAccount(activeCourse.owner_account_id);
        },
        setProjectSettings,

        accountUsers,
        sendInvitations,
        removeUser,

        updateCourseSettings,
        handleProjectCardDropdown,
        handleGenerateId,
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    const projectSettingsDialogProps = {
        open: projectSettings.isOpen,
        onClose: () => setProjectSettings({ isOpen: false }),
    };
    const projectSettingsProps = {
        isOpen: projectSettings.isOpen,
        project: getProject(activeCourse),
        updateProjectSettings,
        onClose: () => setProjectSettings({ isOpen: false }),
    };

    const responsiveProjectSettings = {
        mobile: (
            <ResponsiveDialog {...projectSettingsDialogProps}>
                <ProjectSettings device={'mobile'} {...projectSettingsProps} />
            </ResponsiveDialog>
        ),
        tablet: (
            <ResponsiveDialog {...projectSettingsDialogProps}>
                <ProjectSettings device={'tablet'} {...projectSettingsProps} />
            </ResponsiveDialog>
        ),
        desktop: (
            <ResponsiveDialog {...projectSettingsDialogProps}>
                <ProjectSettings device={'desktop'} {...projectSettingsProps} />
            </ResponsiveDialog>
        ),
    };

    return (
        <>
            {withResponsiveLayout(width, responsiveLayouts)}
            {withResponsiveLayout(width, responsiveProjectSettings)}
            <AlertDialog
                message={dialog.message}
                description={dialog.description}
                open={dialog.open}
                variant={dialog.variant}
                actionButtonText={dialog.actionButtonText}
                onAction={dialog.onAction}
                onCancel={() => setDialog((current) => ({ ...current, open: false }))}
                onClose={() => setDialog((current) => ({ ...current, open: false }))}
            />
            <CloningContents
                open={cloningContentsDialog.open}
                setOpen={setCloningContentsDialog}
                course={getActiveCourse(activeCourse)}
                dialog={dialog}
                setDialog={setDialog}
                generatedIdList={generatedIdList}
                setGeneratedIdList={setGeneratedIdList}
            />
        </>
    );
};

export default PageLayout;
